import React from "react";
import LayoutHOC from "../LayoutHOC";
import { Link } from "react-router-dom";
import { useParams } from "react-router-dom";
import useApplyedJobDetails from "../../hooks/useApplyedJobDetails";
import { PROFILE_PIC_URL } from "../../utils/constants";

const ApplyedJobDetails = () => {
  const { id } = useParams();
  const response = useApplyedJobDetails(id);
  const details = response?.data;

  const formatStatus = (status) => {
    if (!status) return "Applied";
    const map = {
      submitted: "Applied",
      applied: "Applied",
      pending: "Under Review",
      under_review: "Under Review",
      approved: "Hired",
      hired: "Hired",
      rejected: "Rejected",
    };
    return map[status?.toLowerCase()] || status;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      const d = new Date(dateStr);
      return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString("en-IN", { dateStyle: "medium" });
    } catch {
      return dateStr;
    }
  };

  const getStatusBadgeClass = (status) => {
    const s = (status || "submitted").toLowerCase();
    if (s === "approved" || s === "hired") return "bg-emerald-100 text-emerald-700";
    if (s === "rejected") return "bg-red-100 text-red-700";
    return "bg-amber-100 text-amber-700";
  };

  const skills = details?.skills || [];
  const languages = details?.languages || [];
  const experienceDetails = details?.experience_details || [];
  const educationDetails = details?.education_details || [];



  if (!details) {
    return (
      <main className="p-4 mt-14">
        <div className="flex items-center justify-center min-h-64">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-500">Loading application details...</p>
          </div>
        </div>
      </main>
    );
  }

  const InfoItem = ({ icon, label, value }) => (
    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
      <div className="p-1.5 rounded-lg bg-white shadow-sm text-teal-600">
        <i className={`bi ${icon} text-lg`}></i>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
        <p className="font-medium text-gray-900 mt-0.5 truncate">{value || "N/A"}</p>
      </div>
    </div>
  );

  return (
    <main className="p-4 mt-14 min-h-screen bg-gradient-to-br from-slate-50 to-teal-50">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link
            to="/applyed_job"
            className="p-2 rounded-lg bg-white shadow-sm border border-gray-100 hover:bg-gray-50 transition-colors"
          >
            <i className="bi bi-arrow-left text-gray-600"></i>
          </Link>
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-teal-700 to-cyan-600 bg-clip-text text-transparent">
              Applied Job Details
            </h1>
            <p className="text-gray-500 text-sm">
              {details?.profession} · {details?.employer_name} ({details?.company_name})
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Section A: Candidate Personal Details */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <i className="bi bi-person-vcard text-teal-500"></i>
              Candidate Personal Details
            </h3>
            <div className="flex flex-col sm:flex-row gap-4 mb-4">
              <div className="w-24 h-24 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
                {details?.profilepic ? (
                  <img
                    src={details.profilepic}
                    alt={details?.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <i className="bi bi-person text-3xl text-gray-400"></i>
                  </div>
                )}
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-gray-900">{details?.name}</h2>
                <p className="text-teal-600 font-medium">{details?.profession}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <InfoItem icon="bi-telephone" label="Phone Number" value={details?.phone} />
              <InfoItem icon="bi-envelope" label="Email Address" value={details?.email} />
              <InfoItem icon="bi-geo-alt" label="Address" value={details?.address} />
              <InfoItem icon="bi-calendar3" label="Date of Birth (DOB)" value={details?.dob ? formatDate(details.dob) : null} />
              <InfoItem icon="bi-gender-ambiguous" label="Gender" value={details?.gender} />
              <InfoItem icon="bi-briefcase" label="Experience" value={details?.experience} />
              <InfoItem icon="bi-mortarboard" label="Education" value={details?.education} />
              <InfoItem icon="bi-building" label="Profession" value={details?.profession} />
            </div>
          </div>
          {/* Education Details */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <i className="bi bi-mortarboard-fill text-teal-500"></i>
              Education
            </h3>

            {educationDetails.length > 0 ? (
              <div className="space-y-4">
                {educationDetails.map((edu, index) => (
                  <div
                    key={index}
                    className="border border-gray-100 rounded-xl p-4 bg-gray-50"
                  >
                    <div className="flex justify-between flex-wrap gap-2">
                      <div>
                        <p className="font-semibold text-gray-900">
                          {edu.courseName || "N/A"}
                        </p>
                        <p className="text-sm text-gray-600">
                          {edu.collageName || "N/A"}
                        </p>
                      </div>

                      <span className="text-xs px-2 py-1 rounded-lg bg-cyan-100 text-cyan-700 font-medium">
                        {edu.currentlyPursuing == 1 ? "Pursuing" : "Completed"}
                      </span>
                    </div>

                    <p className="text-sm text-gray-500 mt-2">
                      {edu.startDate
                        ? formatDate(edu.startDate)
                        : "N/A"}{" "}
                      -{" "}
                      {edu.currentlyPursuing == 1
                        ? "Present"
                        : edu.endDate
                        ? formatDate(edu.endDate)
                        : "N/A"}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-sm">No education details available</p>
            )}
          </div>

          {/* Section B: Work Experience */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <i className="bi bi-briefcase-fill text-teal-500"></i>
              Work Experience
            </h3>

            {experienceDetails.length > 0 ? (
              <div className="space-y-4">
                {experienceDetails.map((exp, index) => (
                  <div
                    key={index}
                    className="border border-gray-100 rounded-xl p-4 bg-gray-50"
                  >
                    <div className="flex justify-between flex-wrap gap-2">
                      <div>
                        <p className="font-semibold text-gray-900">
                          {exp.designation || "N/A"}
                        </p>
                        <p className="text-sm text-gray-600">
                          {exp.company || "N/A"}
                        </p>
                      </div>

                      <span className="text-xs px-2 py-1 rounded-lg bg-teal-100 text-teal-700 font-medium">
                        {exp.currentlyWorking == 1 ? "Current Job" : "Past"}
                      </span>
                    </div>

                    <p className="text-sm text-gray-500 mt-2">
                      {exp.startDate
                        ? formatDate(exp.startDate)
                        : "N/A"}{" "}
                      -{" "}
                      {exp.currentlyWorking == 1
                        ? "Present"
                        : exp.endDate
                        ? formatDate(exp.endDate)
                        : "N/A"}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-sm">No experience details available</p>
            )}
          </div>

          {/* Section B: Skills & Profile Information */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <i className="bi bi-tools text-teal-500"></i>
              Skills & Profile Information
            </h3>
            <div className="space-y-4">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Skills</p>
                <div className="flex flex-wrap gap-2">
                  {skills.length > 0 ? (
                    skills.map((skill, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center rounded-lg bg-teal-50 px-3 py-1.5 text-sm font-medium text-teal-700 ring-1 ring-teal-600/10"
                      >
                        {skill?.name || skill}
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-400 text-sm">No skills listed</span>
                  )}
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Languages</p>
                <div className="flex flex-wrap gap-2">
                  {languages.length > 0 ? (
                    languages.map((lang, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center rounded-lg bg-cyan-50 px-3 py-1.5 text-sm font-medium text-cyan-700 ring-1 ring-cyan-600/10"
                      >
                        {lang?.name || lang}
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-400 text-sm">No languages listed</span>
                  )}
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Description / Introduction</p>
                <p className="text-gray-700 leading-relaxed rounded-lg bg-gray-50 p-4">
                  {details?.about || details?.description || "No description available."}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Section C: Job Application Status - Sidebar */}
        <div className="space-y-6">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <i className="bi bi-clipboard-check text-teal-500"></i>
              Job Application Status
            </h3>
            <div className="space-y-4">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Current Status</p>
                <span
                  className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium ${getStatusBadgeClass(
                    details?.application_status
                  )}`}
                >
                  <i className="bi bi-circle-fill text-[8px] mr-2"></i>
                  {formatStatus(details?.application_status)}
                </span>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Date of Status Update</p>
                <p className="font-medium text-gray-900">
                  {details?.status_updated_at ? formatDate(details.status_updated_at) : "N/A"}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl p-6 text-white">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-white/20 rounded-lg">
                <i className="bi bi-briefcase text-xl"></i>
              </div>
              <h3 className="font-semibold">Job Info</h3>
            </div>
            <div className="space-y-2 text-sm">
              <p><span className="text-white/70">Position:</span> {details?.profession}</p>
              <p><span className="text-white/70">Employer:</span> {details?.employer_name}</p>
              <p><span className="text-white/70">Company:</span> {details?.company_name}</p>
              <p><span className="text-white/70">Deadline:</span> {formatDate(details?.deadline)}</p>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default LayoutHOC(ApplyedJobDetails);
